import { CommonModule } from '@angular/common';
import { Component, inject, input, model, output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { Menu, MenuModule } from 'primeng/menu';
import { Table, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { IamActionLabels } from 'src/app/models/permission';
import { Column, TableInfo } from 'src/app/models/table';
import { TranslationPipe } from 'src/app/pipes/translation/translation-pipe';
import { TranslationService } from 'src/app/services/configuration/translation-service';
import { PermissionService } from 'src/app/services/user/permission-service';
import { StatusClassPipe } from '../../../pipes/status-class/status-class-pipe';
import { AppButton } from '../../button/app-button';

@Component({
  selector: 'app-recursive-table',
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    CheckboxModule,
    AppButton,
    MenuModule,
    TagModule,
    FormsModule,
    TranslationPipe,
    TooltipModule,
    StatusClassPipe
  ],
  templateUrl: './recursive-table.html',
  styleUrl: './recursive-table.scss'
})
export class AppRecursiveTable {
  permissionService = inject(PermissionService);
  translationService = inject(TranslationService);

  @ViewChild('recursiveTable') recursiveTable!: Table;

  data = input<any[]>([]);
  tableInfo = input<TableInfo>(new TableInfo());
  moduleCode = input<string>('');
  columnWidths = input<number[]>([]);
  onExpandClick = output<any>();
  onChildRowClick = output<any>();
  onChildRowClickItem = output<any>();
  onEditClick = output<any>();
  onMoreClick = output<any>();
  onMoreItemClick = output<any>();
  checkboxStateMap = model<{ [id: number]: boolean }>({});

  allChecked: boolean = false;
  enableStickyCols: boolean = true; //auto detect if there are any sticky columns
  activeMoreMenu: any | null = null;

  ngOnInit() {
    this.enableStickyCols = this.tableInfo().columns.some((col) => col.sticky === true);
  }

  ngOnChanges() {
    // this.adjustTableState();
  }

  //realign frozen columns after data change (sometimes if data length is too long, frozen columns misaligns)
  adjustTableState() {
    if (!this.recursiveTable) return;

    this.recursiveTable.resetScrollTop?.();

    // Wait for DOM to update
    setTimeout(() => {
      const root = (this.recursiveTable as any).el?.nativeElement || this.recursiveTable.el;
      if (!root) return;

      // const frozenCols = Array.from(root.querySelectorAll('.p-datatable-tbody .p-datatable-frozen-column')) as HTMLElement[];
      // console.log('frozen: ', frozenCols);

      const container = root.querySelector('.p-datatable-table-container');
      if (!container) return;

      const frozenCols = Array.from(container.querySelectorAll('.p-datatable-tbody .p-datatable-frozen-column')) as HTMLElement[];

      let leftOffset = 0;
      for (const col of frozenCols) {
        col.style.left = `${leftOffset}px`;
        leftOffset += col.offsetWidth;
      }
    }, 0);
  }

  onExpand(event: any, item: any, expanded: boolean) {
    event.stopPropagation();
    if (!expanded) this.emitExpand(item);
  }

  getCheckboxState(id: number): boolean {
    return (!this.isEmptyCheckboxStateMap() && this.checkboxStateMap()[id]) ?? false;
  }

  isEmptyCheckboxStateMap() {
    if (!this.checkboxStateMap() || Object.keys(this.checkboxStateMap()).length === 0) {
      return true;
    }

    return false;
  }

  onCheckboxChange(item: any, event: any) {
    // if (event.checked) {
    //   this.checkboxStateMap.update(map => ({
    //     ...map,
    //     [item.id]: true
    //   }));
    // }
    // else {
    //   delete this.checkboxStateMap()[item.id];
    // }
    this.checkboxStateMap.update((map) => ({ ...map, [item.id]: event.checked }));
    if (this.checkboxStateMap()[item.id] === false) {
      delete this.checkboxStateMap()[item.id];
    }
    const dataItem = this.data().find((d) => d.id === item.id);
    if (dataItem) dataItem.checked = this.checkboxStateMap()[item.id];

    this.allChecked = this.areAllCheckboxesSelected();
  }

  areAllCheckboxesSelected() {
    return this.data().every((item) => item.checked);
  }

  onChildRowClickEvent(item: any) {
    this.onChildRowClick.emit(item.id);
    this.onChildRowClickItem.emit(item);
  }

  onEditClickEvent(item: any) {
    this.onEditClick.emit(item);
  }

  async onMoreClickEvent(event: MouseEvent, item: any, menu: Menu) {
    event.stopPropagation();

    let moreOptions: any;

    if (this.tableInfo().moreOptionsType === 'item-based') {
      moreOptions = item.moreOptions;
    } else {
      moreOptions = this.tableInfo().moreOptions;
    }

    // close previously opened menu
    if (this.activeMoreMenu && this.activeMoreMenu !== menu) {
      this.activeMoreMenu.hide();
    }

    moreOptions = (
      await Promise.all(
        moreOptions.map(async (option: any) => {
          if (option.separator) {
            return option;
          }

          option.value = option.value ?? option.label;

          const label = option.label ?? '';
          const required = this.permissionService.getTypeOfPermissionBasedOnLabel(label);

          if (!required) {
            return option;
          }

          const allowed = this.moduleCode()
            ? await this.permissionService.hasPermission(
              this.moduleCode(),
              required
            )
            : false;

          return allowed ? option : null;
        })
      )
    ).filter((option: any) => option !== null)
      .map((option: any) => {
        if (option.separator) {
          return { separator: true };
        }

        const label = option.label ?? '';

        return {
          ...option,
          disabled:
            item.disableMenuOption === true || option.disabled === true,
          label: this.translationService.translate(label),
          command: () => {
            this.onMoreItemClick.emit({
              item: item,
              action: option.value
            });

            menu.hide();
            this.activeMoreMenu = null;
          }
        };
      });

    const moreOptionsEmpty = moreOptions.length === 0 || (moreOptions.every((m: any) => m.separator === true));
    if (moreOptionsEmpty) {
      item.moreOptions = [{
        disabled: true,
        label: this.translationService.translate('NO_PERMISSION'),
      }];
    }
    else {
      const shouldRemoveSeparator = moreOptions.length === 2 && moreOptions.some((m: any) => m.separator === true)
      item.moreOptions = shouldRemoveSeparator ? moreOptions.filter((m: any) => m.separator !== true) : moreOptions;

    }

    // toggle current menu
    menu.toggle(event);
    this.activeMoreMenu = menu;
  }

  emitExpand(item: any) {
    this.onExpandClick.emit(item);
  }

  buildHeaderParams(col: Column) {
    if (!col.headerTranslationParams || col.headerTranslationParams.length === 0) {
      return {}; // no params → translator works normally
    }

    // Convert ["MYR", "year"] to {0: "MYR", 1: "year"}
    return col.headerTranslationParams.reduce((acc, val, index) => {
      acc[index] = val;
      return acc;
    }, {} as any);
  }
}
